import { ColumnType } from "antd/es/table";
import { DisplayAmount } from "ui";
import { dateFormat, getFilterIcon, getSortIcon } from "utils";
import {
  getEnumKeys,
  RUB_OPERATION_TYPE_ENUM,
  RUB_OPERATION_TYPE_TEXT_OPERATOR,
  RUB_OPERATION_TYPE_TEXTS,
  RubOperationContent,
} from "interfaces";
import { OperationTypeIcon } from "./OperationTypeIcon";
import { IStores } from "stores";
import { Flex, Tooltip, Typography } from "antd";
import { ReactComponent as InfoIcon } from "images/info.svg";

const negativeOperationTypes = [
  RUB_OPERATION_TYPE_ENUM.OUT,
  RUB_OPERATION_TYPE_ENUM.TRANSFER_OUT,
  RUB_OPERATION_TYPE_ENUM.LOCK,
  RUB_OPERATION_TYPE_ENUM.LOCK_WITHDRAWAL,
  RUB_OPERATION_TYPE_ENUM.FEE,
];

function formatAmount(type: RUB_OPERATION_TYPE_ENUM, amount: number): number {
  return negativeOperationTypes.includes(type) ? -amount : amount;
}

export const getColumns = ({
  user,
  rubOperations,
}: {
  user: IStores["user"];
  rubOperations: IStores["rubOperations"];
}): Array<ColumnType<RubOperationContent>> => {
  const { isOperator } = user.roles;

  return [
    {
      dataIndex: "hid",
      key: "hid",
      title: "Номер заявки",
      render: (_: any, record: RubOperationContent) => {
        return record.hid ? `№${record.hid}` : "—";
      },
      width: 50,
    },
    isOperator && {
      dataIndex: "companyName",
      key: "companyName",
      title: "Компания",
      render: (_: any, record: RubOperationContent) => {
        return record.companyName ?? "—";
      },
      width: 170,
    },
    {
      dataIndex: "types",
      key: "types",
      title: "Тип",
      filterMultiple: true,
      defaultFilteredValue: rubOperations.queryParams["types"],
      filtered: "types" in rubOperations.queryParams,
      filterIcon: (filtered: boolean) => getFilterIcon(filtered),
      filters: getEnumKeys(RUB_OPERATION_TYPE_ENUM).map((key) => ({
        text: isOperator ? RUB_OPERATION_TYPE_TEXT_OPERATOR[key] : RUB_OPERATION_TYPE_TEXTS[key],
        value: key,
      })),

      render: (_: any, record: RubOperationContent) => {
        return (
          <>
            <OperationTypeIcon type={record.type} isOperator={isOperator} />
          </>
        );
      },
      width: 150,
    },

    {
      dataIndex: "amount",
      key: "amount",
      title: "Сумма",
      sorter: !isOperator,
      width: 250,
      sortIcon: !isOperator && getSortIcon,
      render: (_: any, record: RubOperationContent) => {
        const amount = formatAmount(record.type, record.amount);

        return (
          <DisplayAmount
            amount={amount || 0}
            currency={"RUB"}
            currencyList={{ RUB: "Российский рубль" }}
            showFlag
            squareBg
            withName
          />
        );
      },
    },
    {
      dataIndex: "proofOfPayment",
      key: "proofOfPayment",
      title: "№ п/п",
      render: (_: any, record: RubOperationContent) => {
        const tooltipTitle =
          record.purpose && record.proofOfPayment
            ? `№ п/п: ${record.proofOfPayment} - ${record.purpose}`
            : `${record.purpose || record.proofOfPayment}`;

        const showTooltip = record.purpose || record.proofOfPayment;
        return (
          <Flex gap="4px">
            {record.proofOfPayment ?? "—"}
            {showTooltip && (
              <Tooltip title={tooltipTitle}>
                <InfoIcon style={{ width: "10px", height: "10px" }} />
              </Tooltip>
            )}
          </Flex>
        );
      },
      width: 150,
    },

    {
      dataIndex: "date",
      key: "date",
      title: "Дата",
      align: "left",
      sorter: true,
      width: 200,
      sortIcon: getSortIcon,
      render: (_: any, record: RubOperationContent) => {
        return dateFormat(record.date, true);
      },
    },
  ].filter((col) => !!col) as Array<ColumnType<RubOperationContent>>;
};
